/**
 * shared/dashboard-types.ts
 *
 * Shared types for org + platform dashboards.
 * KPI shapes, tab identifiers, activity feed entries,
 * filter/sort/pagination, and tour completion state.
 */

import { z } from "zod";
import { QUERY_SORT_DIRECTIONS } from "./analytics-types";

// ---------------------------------------------------------------------------
// Dashboard context
// ---------------------------------------------------------------------------

export type DashboardContextType = "org" | "platform";

export interface OrgDashboardContext {
  readonly type: "org";
  readonly orgId: string;
}

export interface PlatformDashboardContext {
  readonly type: "platform";
}

export type DashboardContext = OrgDashboardContext | PlatformDashboardContext;

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

// Redesigned Organisation hub (spec 2026-08-21-org-dashboard). Four
// destinations, no third navigation level:
//   home      — the signal layer: what needs you, and what is worth a look.
//   people    — one roster; signing-key facts render on the person they belong
//               to, which is why the former "governance" tab has no successor
//               of its own (spec §Information architecture).
//   approvals — the queue of record for changes awaiting a signature.
//   settings  — org-wide rules; admin-only, absent (not disabled) for others.
// Retired ids `overview` | `members` | `governance` redirect via
// `resolveDashboardTabRedirect` in client/src/App.tsx — see module 01 Task 2.
export const ORG_TAB_IDS = [
  "home",
  "people",
  "approvals",
  "settings",
] as const;
export type OrgTabId = (typeof ORG_TAB_IDS)[number];

// Five sections named for what an operator wants, not for the system behind
// them. "audit" is no longer a tab: Activity is reached from Home, from an
// organisation, from a person, and from its own bottom-bar tab, always
// arriving pre-filtered. Spec §Information architecture.
//
// The warehouse-backed "analytics" tab was removed from the platform console —
// its four charts now render as a section on the platform Overview (the 30k-ft
// landing view), so operators reach them without a dedicated tab and the tab bar
// carries only what an operator actively manages with.
//
// `action-policy` arrived on main via #2774 while this redesign was in flight
// and is deliberately NOT a sixth tab: the five names above are the whole
// platform tab set (AC-1). Its console renders inside the "Platform access"
// section (who may do what, and at what tier, is one question), and the
// retired id resolves there via LEGACY_PLATFORM_TAB_REDIRECTS.
export const PLATFORM_TAB_IDS = [
  "overview",
  "orgs",
  "access",
  "features",
  "status",
] as const;
export type PlatformTabId = (typeof PLATFORM_TAB_IDS)[number];

// "history" is an operator-only tab that appears ONLY in the org drill-in
// (`/admin/platform/org/:orgId`) — it is deliberately NOT in ORG_TAB_IDS (so a
// regular member's /dashboard never shows it) nor PLATFORM_TAB_IDS (it needs an
// orgId). The shell injects it for the operator org view; the type union admits
// it so route/tab params stay typed.
//
// "audit" is deliberately ABSENT (module 02 task 3): it is not a tab on any
// surface any more. Activity lives at `/admin/platform/activity`, its own
// route; `/admin/platform/audit` redirects there (admin-routes.tsx). A KPI or
// link whose destination is that page carries `KpiValue.linksToPath`, not a
// tab id.
export type TabId = OrgTabId | PlatformTabId | "history";

// ---------------------------------------------------------------------------
// Platform operator — org lifecycle + directory rows
// ---------------------------------------------------------------------------

/** Lifecycle states surfaced in the operator Organizations directory. */
export const PLATFORM_ORG_LIFECYCLE_STATUSES = [
  "invited",
  "pending_approval",
  "approved",
  "awaiting_vetting",
  "vetted",
  "suspended",
] as const;
export type PlatformOrgLifecycleStatus =
  (typeof PLATFORM_ORG_LIFECYCLE_STATUSES)[number];

/**
 * One row in the merged org directory (orgs + pending invites). The read layer
 * merges two queries with this `kind` discriminator — never a SQL UNION (AC-13).
 * The route projects the server `DirectoryEntry` into this shape; member counts
 * are intentionally absent (the directory query joins no memberships, and a
 * fabricated count would mislead — no Members column).
 */
export type PlatformDirectoryRow =
  | {
      readonly kind: "org";
      readonly orgId: string;
      readonly name: string;
      readonly domain: string | null;
      readonly lifecycle: PlatformOrgLifecycleStatus;
      readonly createdAt: string;
    }
  | {
      readonly kind: "invite";
      readonly inviteId: string;
      readonly name: string;
      readonly domain: string;
      readonly lifecycle: "invited";
      readonly createdAt: string;
      readonly expiresAt: string;
      /**
       * Which rail minted this invite (migration 0131). `"legacy"` rows live in
       * `platform_org_invites` and are the only ones the resend/revoke operator
       * routes can act on; `"counterparty"` rows are platform-sourced
       * `counterparty_invites`. The client hides the Resend action on the
       * latter rather than offering a button that would 404.
       */
      readonly rail: "legacy" | "counterparty";
    };

// ---------------------------------------------------------------------------
// Per-org onboarding lifecycle (operator History view)
// ---------------------------------------------------------------------------

/**
 * One milestone on an organisation's onboarding journey. `at` is the ISO
 * timestamp the milestone was reached, or `null` when it hasn't been reached
 * yet. `actorName` (when present) is the operator/officer attributed to the
 * step — resolved from `profiles.display_name`, never a raw user id (Rule 9).
 */
export interface OrgLifecycleMilestone {
  readonly key: string;
  readonly label: string;
  readonly at: string | null;
  readonly actorName?: string | null;
  readonly done: boolean;
}

/**
 * An organisation's onboarding journey + current lifecycle status, for the
 * operator per-org History view. Reconstructed from `org_nodes` timestamp
 * columns (no separate transition-audit table exists); the current `status`
 * reuses the same derivation as the directory badge.
 */
export interface PlatformOrgLifecycle {
  readonly orgId: string;
  readonly name: string;
  readonly domain: string | null;
  readonly status: PlatformOrgLifecycleStatus;
  readonly createdAt: string;
  readonly milestones: ReadonlyArray<OrgLifecycleMilestone>;
}

// ---------------------------------------------------------------------------
// KPI card data
// ---------------------------------------------------------------------------

export interface KpiValue {
  readonly label: string;
  readonly value: number | string;
  readonly tooltip: string;
  readonly delta?: number;
  readonly deltaWindow?: string;
  readonly linksToTab: TabId;
  /**
   * Destination when the card's real target is a standalone page rather than
   * one of the shell's tabs — today only Activity
   * (`/admin/platform/activity`), which stopped being a tab in module 02.
   * When set it WINS over `linksToTab`, which stays required as the in-shell
   * fallback (`linksToFilter` is ignored on this path: a different page owns
   * its own filter state).
   */
  readonly linksToPath?: string;
  readonly linksToFilter?: string;
  readonly updatedAt: string; // ISO timestamp
}

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------

export interface ActivityFeedEntry {
  readonly id: string;
  readonly eventType: string;
  readonly title: string;
  readonly status: "success" | "warning" | "neutral" | "info";
  readonly timestamp: string;
  readonly actorHash: string;
  readonly detailUrl: string;
  readonly pill?: string;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export const dashboardFiltersSchema = z.object({
  aal: z.enum(["AAL1", "AAL2", "AAL3"]).optional(),
  status: z.enum(["active", "pending", "suspended", "flagged"]).optional(),
  severity: z.enum(["standard", "elevated", "critical"]).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(25),
  // No `sortBy`: the order column is fixed per table server-side, never chosen
  // by the request (zod strips an unknown key, so a sent `sortBy` is ignored).
  sortDir: z.enum(QUERY_SORT_DIRECTIONS).default("desc"),
});
export type DashboardFilters = z.infer<typeof dashboardFiltersSchema>;

// ---------------------------------------------------------------------------
// Tour completion
// ---------------------------------------------------------------------------

export const TOUR_IDS = [
  "end_user_walkthrough",
  "org_admin_dashboard",
  "strem_admin_platform",
] as const;
export type TourId = (typeof TOUR_IDS)[number];

export interface TourCompletion {
  readonly tourId: TourId;
  readonly completedAt: string | null;
  readonly skippedAt: string | null;
}
